/**
 * `<TableOfContents>` — hover outline for the Progress Note. At rest it is a
 * one-line horizontal strip (one tick per section, the in-view section in
 * coral, plus its name); hovering or focusing it reveals a floating two-level
 * "On this page" panel below, as an overlay that never shifts layout. Used by
 * both `<ProgressNoteViewer>` (read mode) and `<ProgressNoteEdit>`.
 *
 * Links come from `tocRegistry`: static order and labels, filtered to the
 * headings currently mounted. Sections and subsections hide themselves when
 * empty, so this is what keeps the list free of anchors that scroll nowhere.
 */
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { Text } from '@mantine/core'
import { PROGRESS_NOTE_CORAL as CORAL } from '../ProgressNoteEdit/tokens'
import { useTocEntries, type TocEntry } from './tocRegistry'

type TocGroup = {
  section: TocEntry
  subsections: TocEntry[]
}

/** Subsections attach to the section above them; both arrive in document order. */
function groupEntries(entries: TocEntry[]): TocGroup[] {
  const groups: TocGroup[] = []
  for (const entry of entries) {
    if (entry.level === 'section') {
      groups.push({ section: entry, subsections: [] })
    } else {
      groups[groups.length - 1]?.subsections.push(entry)
    }
  }
  return groups
}

/** Id of the heading currently at the reading line, for the in-view marker. */
function useActiveEntry(
  entries: TocEntry[],
  anchorRef: RefObject<HTMLDivElement | null>,
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    const headings = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((el): el is HTMLElement => Boolean(el))
    if (headings.length === 0) return

    const pick = () => {
      // Last heading above the reading line: 25% into the area below the
      // strip. Measured off the strip itself (not the window) so the inner
      // chart scrollport and any sticky offset are respected.
      const anchor = anchorRef.current?.getBoundingClientRect().bottom ?? 0
      const line = anchor + (window.innerHeight - anchor) * 0.25
      let current: HTMLElement | null = null
      for (const el of headings) {
        if (el.getBoundingClientRect().top <= line) current = el
      }
      setActiveId((current ?? headings[0]).id)
    }

    pick()
    // The chart scrolls in an inner container, so listen with capture to see
    // scroll events from any ancestor.
    window.addEventListener('scroll', pick, { capture: true, passive: true })
    window.addEventListener('resize', pick)
    return () => {
      window.removeEventListener('scroll', pick, { capture: true })
      window.removeEventListener('resize', pick)
    }
  }, [entries, anchorRef])

  return activeId
}

function PanelLink({
  entry,
  active,
  sub,
  onNavigate,
}: {
  entry: TocEntry
  active: boolean
  sub?: boolean
  onNavigate: (id: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(entry.id)}
      data-testid={`toc-link-${entry.id}`}
      className={[
        'block w-full cursor-pointer border-l-2 text-left transition-colors',
        sub ? 'py-[3px] pl-6 text-[12px]' : 'py-[4px] pl-3 text-[13px]',
        active
          ? 'border-[var(--toc-accent)] font-semibold text-gray-900'
          : 'border-transparent hover:text-gray-900 ' +
            (sub ? 'font-medium text-gray-500' : 'font-medium text-gray-700'),
      ].join(' ')}
    >
      {entry.label}
    </button>
  )
}

type Props = {
  /** Sticky offset in px — raise it when another sticky bar owns the top edge
      of the same scrollport (edit mode's Save/Cancel bar). */
  stickyTopPx?: number
}

export function TableOfContents({ stickyTopPx = 0 }: Props) {
  const entries = useTocEntries()
  const rootRef = useRef<HTMLDivElement>(null)
  const activeId = useActiveEntry(entries, rootRef)
  const [open, setOpen] = useState(false)

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // The sticky strip (plus edit's Save/Cancel bar) overlays the scrollport
    // top — the jump target must stop short of it or the heading hides under it.
    el.style.scrollMarginTop = `${(rootRef.current?.offsetHeight ?? 0) + stickyTopPx + 8}px`
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Mouse users close via mouseleave — this covers touch taps and clicks
  // landing anywhere outside the strip and panel.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  if (entries.length === 0) return null

  const groups = groupEntries(entries)
  const activeSection = groups.find(
    ({ section, subsections }) =>
      section.id === activeId || subsections.some((sub) => sub.id === activeId),
  )?.section

  return (
    <div
      ref={rootRef}
      // Sticky: docks at the chart scroll-container's top edge (which already
      // sits below the fixed header stack) while the note scrolls, so the
      // progress ticks and outline stay reachable mid-note.
      className="sticky z-30 bg-white py-1"
      style={{ top: stickyTopPx, '--toc-accent': CORAL } as CSSProperties}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlurCapture={(event) => {
        // Close when keyboard focus moves outside the strip and panel.
        if (
          !(event.relatedTarget instanceof Node) ||
          !rootRef.current?.contains(event.relatedTarget)
        ) {
          setOpen(false)
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false)
      }}
      data-testid="progress-note-toc"
    >
      {/* Horizontal strip: one tick per section (coral = in view) plus the
          in-view section's name — a reading-progress line at rest. */}
      <button
        type="button"
        aria-label="Table of contents"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        className="-mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5"
      >
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          On this page
        </Text>
        <span className="flex items-center gap-[5px]">
          {groups.map(({ section }) => (
            <span
              key={section.id}
              className={[
                'h-[3px] rounded-full transition-all duration-150',
                section.id === activeSection?.id
                  ? 'w-7 bg-[var(--toc-accent)]'
                  : 'w-4 bg-slate-300',
              ].join(' ')}
            />
          ))}
        </span>
        {activeSection && (
          <span className="text-[12.5px] font-medium text-gray-700">{activeSection.label}</span>
        )}
      </button>

      {/* Floating outline — absolutely anchored below the strip so opening
          never shifts layout; the pt-1 bridge keeps the hover alive between
          strip and panel. */}
      <div
        className={[
          'absolute top-full left-0 z-40 pt-1 transition-opacity duration-150 motion-reduce:transition-none',
          open ? 'visible opacity-100' : 'invisible opacity-0',
        ].join(' ')}
      >
        <div className="max-h-[60vh] w-[280px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
          {groups.map(({ section, subsections }) => (
            <div key={section.id}>
              <PanelLink entry={section} active={activeId === section.id} onNavigate={jumpTo} />
              {subsections.map((subsection) => (
                <PanelLink
                  key={subsection.id}
                  entry={subsection}
                  active={activeId === subsection.id}
                  sub
                  onNavigate={jumpTo}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
